export const tableSorter = (a, b, key) => a?.[key]?.localeCompare(b?.[key]);

// Sorting direction constants
export const SORT_DIRECTION = {
  ASC: "asc",
  DESC: "desc",
  ASCEND: "ascend",
  DESCEND: "descend",
};

/**
 * antd column `sortOrder` <- API ordering.
 * Returns "ascend"/"descend" when the API is ordering by `field`, else null
 * (null = the column's unsorted state, i.e. antd's 3rd click).
 */
export const toAntdSortOrder = (orderBy, orderDirection, field) => {
  if (orderBy !== field) return null;
  return orderDirection === SORT_DIRECTION.DESC ? SORT_DIRECTION.DESCEND : SORT_DIRECTION.ASCEND;
};

/**
 * API ordering <- antd `sorter.order`.
 * antd sends "ascend" | "descend" | undefined (undefined = sort cleared).
 * Returns undefined when cleared so no ordering is sent.
 */
export const toApiSortDirection = antdOrder => {
  if (!antdOrder) return undefined;
  return antdOrder === SORT_DIRECTION.DESCEND ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;
};

/**
 * `scroll.x` for a fixed-layout table: the sum of every column's `width`
 * (plus `extra`, e.g. a selection column).
 *
 * Pair with `tableLayout="fixed"` and an explicit `width` on EVERY column. AntD
 * then honours the widths exactly instead of re-measuring cell content on each
 * render — which is what makes columns jump when data changes on sort / filter /
 * page — and narrow screens get a horizontal scroll instead of squashed columns.
 */
export const getFixedTableScrollX = (columns = [], extra = 0) =>
  columns.reduce((sum, col) => sum + (Number(col?.width) || 0), 0) + extra;

/**
 * Set a form field programmatically and clear any stale validation error.
 *
 * `setFieldValue` only writes the value — AntD does not re-run that field's
 * rules, so an error raised by an earlier submit stays on screen even though
 * the field now holds something valid. That is what happens when the user hits
 * Send with an empty subject, then has the AI fill it in: the subject is right
 * there and the form still says it is required.
 *
 * Re-validating just this field clears the message when it now passes, and
 * leaves it in place when it genuinely still fails. `validateFields` rejects on
 * a failing field, which is a reported result and not an error to handle, so
 * the rejection is swallowed.
 */
export const setFieldAndValidate = (form, name, value) => {
  if (!form?.setFieldValue) return;
  form.setFieldValue(name, value);
  form.validateFields?.([name])?.catch?.(() => {});
};
